/** @jsxRuntime classic */
/** @jsx jsx */
import { FC } from 'react';
import { jsx, css } from '@emotion/react';
import styled from '@emotion/styled';
import { ColorName, ContentSection, Text } from '@zapier/design-system';

import { Icon, Props as IconProps } from '../Icon/Icon';
import { mediaQueries } from '../../constants';
import * as Types from '../../global/types';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../sections/types';
import { CtaGoalType, CtaNameType } from '../../observability/tracking/events';
import {
  hiddenOnMobile,
  VisibleOnlyOnMobile,
} from '../../utils/getResponsiveStyles';

import { Link } from './Link';
import { CTA } from './CTA';
import { BlockContextProvider } from './blockContext';
import useBlockContext from './useBlockContext';

export enum BlockHeadingTag {
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
}

export type Props = {
  align?: Types.Align;
  content: RichTextHTMLString;
  contentStyle?: Types.ContentStyleType;
  isLink?: boolean;
  ctaText?: string;
  ctaHref?: string;
  ctaGoal?: CtaGoalType;
  ctaName?: CtaNameType;
  ctaStyle?: Types.CtaStyleType;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
  secondaryCtaGoal?: CtaGoalType;
  secondaryCtaName?: CtaNameType;
  secondaryCtaStyle?: Types.CtaStyleType;
  textPlacement?: Types.TextPlacementType;
  mobileTextPlacement?: Types.MobileTextPlacementType;
  heading?: string;
  headingStyle?: Types.HeadingStyleType;
  headingTag?: BlockHeadingTag;
  media?: React.ReactNode;
  isPageWidth?: boolean;
  smallText?: string;
  superTitle?: string;
  icon?: IconProps['iconName'];
};

/**
 * @deprecated
 * This component has been deprecated. `BlockGridItem` should be used instead.
 */
export function Block({
  isLink = false,
  content,
  ctaHref,
  ctaGoal,
  ctaName,
  ...restProps
}: Props) {
  return (
    <ContentSection>
      <BlockContextProvider>
        {({ onMouseEnter, onMouseDown, onMouseLeave }) =>
          isLink ? (
            <Link
              ctaGoal={ctaGoal!}
              ctaName={ctaName!}
              ctaText={content}
              href={ctaHref!}
              onMouseDown={onMouseDown}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <BlockContent {...restProps} content={content} isLink={true} />
            </Link>
          ) : (
            <BlockContent
              {...restProps}
              content={content}
              ctaGoal={ctaGoal!}
              ctaHref={ctaHref!}
              ctaName={ctaName!}
            />
          )
        }
      </BlockContextProvider>
    </ContentSection>
  );
}

const BlockContent: FC<Props> = ({
  content,
  align = 'left',
  textPlacement = Types.TextPlacement.right,
  mobileTextPlacement = Types.MobileTextPlacement.bottom,
  isLink = false,
  ctaHref,
  ctaGoal,
  ctaName,
  media,
  isPageWidth = false,
  icon,
  ...restProps
}) => (
  <Container
    align={align}
    isPageWidth={isPageWidth}
    mobileTextPlacement={mobileTextPlacement}
    textPlacement={textPlacement}
  >
    {icon && (
      <IconContainer>
        <Icon iconName={icon} />
      </IconContainer>
    )}
    {media && (
      <MediaContainer isPageWidth={isPageWidth}>{media}</MediaContainer>
    )}
    <BlockText
      {...restProps}
      align={align}
      content={content}
      ctaGoal={ctaGoal}
      ctaHref={ctaHref}
      ctaName={ctaName}
      isLink={isLink}
      isPageWidth={isPageWidth}
      mobileTextPlacement={mobileTextPlacement}
      textPlacement={textPlacement}
    />
  </Container>
);

type BlockTextProps = Pick<
  Props,
  | 'align'
  | 'textPlacement'
  | 'mobileTextPlacement'
  | 'content'
  | 'contentStyle'
  | 'isLink'
  | 'ctaText'
  | 'ctaHref'
  | 'ctaGoal'
  | 'ctaName'
  | 'ctaStyle'
  | 'secondaryCtaText'
  | 'secondaryCtaHref'
  | 'secondaryCtaGoal'
  | 'secondaryCtaName'
  | 'secondaryCtaStyle'
  | 'heading'
  | 'headingStyle'
  | 'headingTag'
  | 'isPageWidth'
  | 'smallText'
  | 'superTitle'
>;

function BlockText({
  align = 'left',
  textPlacement = Types.TextPlacement.right,
  mobileTextPlacement = Types.MobileTextPlacement.bottom,
  content,
  contentStyle = Types.ContentStyle.paragraph1Short,
  isLink = false,
  ctaText,
  ctaHref,
  ctaGoal,
  ctaName,
  ctaStyle,
  secondaryCtaText,
  secondaryCtaHref,
  secondaryCtaGoal,
  secondaryCtaName,
  secondaryCtaStyle,
  heading,
  headingStyle = Types.HeadingStyle.pageHeader5,
  // Assume within a Section and / or under appropriate h2
  headingTag = BlockHeadingTag.h3,
  isPageWidth = false,
  smallText,
  superTitle,
}: BlockTextProps) {
  if (!content.length) {
    throw new Error('Block content is required');
  }

  const withHoverColor = (color: ColorName, isHover = false) =>
    isHover ? 'inherit' : color;
  const responsiveContentStyleMap = {
    [Types.ContentStyle.paragraph1]: Types.ContentStyle.paragraph3,
    [Types.ContentStyle.paragraph1Bold]: Types.ContentStyle.paragraph3Bold,
    [Types.ContentStyle.paragraph1Short]: Types.ContentStyle.paragraph3,
    [Types.ContentStyle.paragraph1ShortBold]: Types.ContentStyle.paragraph3Bold,
    [Types.ContentStyle.paragraph3]: Types.ContentStyle.paragraph3,
    [Types.ContentStyle.paragraph3Bold]: Types.ContentStyle.paragraph3Bold,
  };
  const isPrimaryCta = Boolean(ctaText && ctaHref && ctaGoal && ctaName);
  const isSecondaryCta = Boolean(
    secondaryCtaText &&
      secondaryCtaHref &&
      secondaryCtaGoal &&
      secondaryCtaName,
  );

  const context = useBlockContext();

  return (
    <TextContainer
      align={align}
      isPageWidth={isPageWidth}
      mobileTextPlacement={mobileTextPlacement}
      textPlacement={textPlacement}
    >
      {superTitle && (
        <Text
          color={withHoverColor('gray60', context?.isHover)}
          hasTransition={false}
          margin="0 0 5px 0"
          tag="p"
          type="superTitle1"
        >
          {superTitle}
        </Text>
      )}
      {heading && (
        <Text
          color={withHoverColor('gray', context?.isHover)}
          hasTransition={false}
          margin="0 0 10px 0"
          tag={headingTag}
          type={Types.HeadingStyle[headingStyle]}
        >
          {heading}
        </Text>
      )}
      <div css={hiddenOnMobile}>
        <Text
          color={withHoverColor('gray80', context?.isHover)}
          hasTransition={false}
          margin="0 0 10px 0"
          tag="div"
          type={Types.ContentStyle[contentStyle]}
        >
          <RichTextHtml html={content} />
        </Text>
      </div>
      <VisibleOnlyOnMobile>
        <Text
          color={withHoverColor('gray80', context?.isHover)}
          hasTransition={false}
          margin="0 0 10px 0"
          tag="div"
          type={responsiveContentStyleMap[contentStyle]}
        >
          <RichTextHtml html={content} />
        </Text>
      </VisibleOnlyOnMobile>
      {smallText && (
        <div css={hiddenOnMobile}>
          <Text
            color={withHoverColor('gray80', context?.isHover)}
            hasTransition={false}
            margin="0 0 10px 0"
            tag="p"
            type="paragraph3"
          >
            {smallText}
          </Text>
        </div>
      )}
      {smallText && (
        <VisibleOnlyOnMobile>
          <Text
            color={withHoverColor('gray80', context?.isHover)}
            hasTransition={false}
            margin="0 0 10px 0"
            tag="p"
            type="smallPrint1"
          >
            {smallText}
          </Text>
        </VisibleOnlyOnMobile>
      )}
      {isPrimaryCta && !isLink && (
        <CTA
          ctaGoal={ctaGoal!}
          ctaHref={ctaHref!}
          ctaName={ctaName!}
          ctaStyle={ctaStyle}
          ctaText={ctaText!}
        />
      )}
      {isSecondaryCta && !isLink && (
        <SecondaryCtaContainer>
          <CTA
            ctaGoal={secondaryCtaGoal!}
            ctaHref={secondaryCtaHref!}
            ctaName={secondaryCtaName!}
            ctaStyle={secondaryCtaStyle}
            ctaText={secondaryCtaText!}
          />
        </SecondaryCtaContainer>
      )}
    </TextContainer>
  );
}

const alignItems: { [Key in Types.Align]: string } = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};

const IconContainer = styled.div`
  margin-bottom: 20px;
`;

const Container = styled.div<{
  align: Types.Align;
  textPlacement: Types.TextPlacementType;
  mobileTextPlacement: Types.MobileTextPlacementType;
  isPageWidth: boolean;
}>(({ align, textPlacement, mobileTextPlacement, isPageWidth }) => [
  css`
    align-items: ${alignItems[align]};
    display: flex;
    flex-direction: column;
    text-align: ${align};
  `,
  isPageWidth &&
    css`
      width: 100%;
      flex-direction: ${mobileTextPlacement === Types.MobileTextPlacement.top
        ? 'column-reverse'
        : 'column'};

      ${mediaQueries.desktop} {
        align-items: center;
        flex-direction: ${textPlacement === Types.TextPlacement.left
          ? 'row-reverse'
          : 'row'};
      }
    `,
]);

const TextContainer = styled.div<{
  align: Types.Align;
  textPlacement: Types.TextPlacementType;
  mobileTextPlacement: Types.MobileTextPlacementType;
  isPageWidth: boolean;
}>(({ align, textPlacement, mobileTextPlacement, isPageWidth }) => [
  css`
    align-items: ${alignItems[align]};
    display: flex;
    flex-direction: column;
    margin-top: ${mobileTextPlacement === Types.MobileTextPlacement.bottom
      ? '20px'
      : '0'};
    margin-bottom: ${mobileTextPlacement === Types.MobileTextPlacement.top
      ? '20px'
      : '0'};

    ${mediaQueries.desktop} {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  isPageWidth &&
    css`
      text-align: ${align};

      ${mediaQueries.desktop} {
        align-items: flex-start;
        justify-content: center;
        margin-right: ${textPlacement === Types.TextPlacement.left ? 60 : 0}px;
        margin-left: ${textPlacement === Types.TextPlacement.left ? 0 : 60}px;
        text-align: left;
        width: 50%;
      }
    `,
]);

const MediaContainer = styled.div<{
  isPageWidth: boolean;
}>(({ isPageWidth }) => [
  css`
    width: 100%;
  `,
  isPageWidth &&
    css`
      ${mediaQueries.desktop} {
        width: 50%;
      }
    `,
]);

const SecondaryCtaContainer = styled.div`
  margin-top: 20px;
`;
